class HeaderMenu extends HTMLElement {
  constructor () {
    super();

    this.headerMobile = this.querySelector('header-mobile');
    this.headerDesktop = this.querySelector('header-desktop');

    this.header = {
      mobileBurgerButton: this.querySelector( 'button[aria-controls="menu-primary"]' ),
      searchButton: this.querySelector( 'button[data-submenu="search-panel"]' )
    };

    this.mobile = this.headerMobile
      ? {
          overlay: this.headerMobile.querySelector( '[overlay]' ),
          closeMenuBtn: this.headerMobile.querySelector( '#close-menu-button' ),
          mainMenuPanel: this.headerMobile.querySelector( '#main-menu-panel' ),
          searchButton: this.headerMobile.querySelector( 'button[data-submenu="search-panel"]' ),
          submenuLinks: this.headerMobile.querySelectorAll( '[data-submenu]:not(button[data-submenu="search-panel"])' ),
          searchPanel: this.headerMobile.querySelector( '#search-panel' ),
          backButtons: this.headerMobile.querySelectorAll( 'button[data-target]' ),
          submenus: this.headerMobile.querySelectorAll( '[data-parent]' )
        }
      : {};

    this.desktop = {
      headerLinks: this.querySelectorAll('[data-header-link]'),
      headerPanel: this.headerDesktop ? this.headerDesktop.querySelector('[data-header-panel]') : null,
      headerSearch: this.headerDesktop ? this.headerDesktop.querySelector('#header-search') : null,
      searchButton: this.querySelector('[data-header-search]')
    };

    this.firstSection = document.querySelector( '[first-section]' ) ? document.querySelector( '[first-section]' ) : document.querySelector( 'main > .shopify-section:first-child' );
    this.desktopPanels = this.desktop.headerPanel
      ? Array.from( this.desktop.headerPanel.querySelectorAll('[id]') ).filter( panel => panel.id.startsWith( 'header-' ) )
      : [];
    this.fadeHideTimers = new WeakMap();
    this.previouslyPlayingVideos = new Set();
    this.lockedScrollPosition = { top: 0, left: 0 };
    this.originalBodyPaddingRight = '';
    this.scrollLockApplied = false;
    this.focusTrapActive = false;
    this.focusableElements = [];
    this.previousFocus = null;
    this.focusTrapRoot = null;
    this.focusActivationTimeout = null;
    this.isMobileOpen = false;

    this.animationDuration = 500;
    this.forceOpaque = false;
    this.isDesktopOpen = false;
    this.desktopCloseTimeout = null;

    this.announcementBar = document.querySelector( 'announcement-bar' );

    this.handleScroll = this.handleScroll.bind( this );
    this.handleResize = this.handleResize.bind( this );
    this.handleEscape = this.handleEscape.bind( this );
    this.cancelScheduledDesktopClose = this.cancelScheduledDesktopClose.bind( this );
    this.scheduleDesktopClose = this.scheduleDesktopClose.bind( this );
    this.handleFocusOut = this.handleFocusOut.bind( this );
    this.handleDesktopSearchInteraction = this.handleDesktopSearchInteraction.bind( this );
    this.handleFocusTrapKeydown = this.handleFocusTrapKeydown.bind( this );
    this.handleFocusTrapFocusIn = this.handleFocusTrapFocusIn.bind( this );
  }

  connectedCallback () {
    this.setupFadeTargets();
    this.bindDesktopEvents();
    this.bindMobileEvents();

    window.addEventListener( 'scroll', this.handleScroll, { passive: true } );
    window.addEventListener( 'resize', this.handleResize );
    document.addEventListener( 'keyup', this.handleEscape );
    this.addEventListener( 'focusout', this.handleFocusOut );

    requestAnimationFrame( () => this.updateTransparency() );
  }

  disconnectedCallback () {
    window.removeEventListener( 'scroll', this.handleScroll );
    window.removeEventListener( 'resize', this.handleResize );
    document.removeEventListener( 'keyup', this.handleEscape );
    this.removeEventListener( 'focusout', this.handleFocusOut );
    this.deactivateFocusTrap();
    if ( this.focusActivationTimeout ) {
      clearTimeout( this.focusActivationTimeout );
      this.focusActivationTimeout = null;
    }
    this.teardownDesktopEvents();
    this.teardownMobileEvents();
  }

  setupFadeTargets () {
    if ( this.headerDesktop && !this.headerDesktop.classList.contains( 'fade' ) ) {
      this.headerDesktop.classList.add( 'fade' );
    }

    if ( this.desktop.headerPanel && !this.desktop.headerPanel.classList.contains( 'fade' ) ) {
      this.desktop.headerPanel.classList.add( 'fade' );
    }

    if ( this.desktop.headerSearch && !this.desktop.headerSearch.classList.contains( 'fade' ) ) {
      this.desktop.headerSearch.classList.add( 'fade' );
    }

    this.desktopPanels.forEach( panel => {
      if ( !panel.classList.contains( 'fade' ) ) {
        panel.classList.add( 'fade' );
      }
    } );
  }

  bindDesktopEvents () {
    this.desktop.headerLinks.forEach( link => {
      const handleOpen = () => this.openDesktopPanel( link, { shouldFocus: false } );
      const handleClick = event => {
        event.preventDefault();
        this.openDesktopPanel( link, { shouldFocus: true } );
      };
      const handleKeydown = event => {
        const key = event.key;
        if ( key !== 'Enter' && key !== ' ' && key !== 'Spacebar' ) return;
        event.preventDefault();
        this.openDesktopPanel( link, { shouldFocus: true } );
      };

      link.addEventListener( 'mouseenter', handleOpen );
      link.addEventListener( 'click', handleClick );
      link.addEventListener( 'mouseleave', this.scheduleDesktopClose );
      link.addEventListener( 'keydown', handleKeydown );

      link.__headerHandlers = { handleOpen, handleClick, handleKeydown };
    } );

    if ( this.desktop.searchButton ) {
      const handleMouseEnter = () => this.cancelScheduledDesktopClose();
      const handleFocus = () => this.cancelScheduledDesktopClose();
      const handleMouseLeave = () => this.scheduleDesktopClose();
      const handleClick = event => {
        event.preventDefault();
        this.handleDesktopSearchInteraction( event );
      };
      const handleKeydown = event => {
        const key = event.key;
        if ( key !== 'Enter' && key !== ' ' && key !== 'Spacebar' ) return;
        event.preventDefault();
        this.handleDesktopSearchInteraction( event );
      };

      this.desktop.searchButton.addEventListener( 'mouseenter', handleMouseEnter );
      this.desktop.searchButton.addEventListener( 'focus', handleFocus );
      this.desktop.searchButton.addEventListener( 'mouseleave', handleMouseLeave );
      this.desktop.searchButton.addEventListener( 'click', handleClick );
      this.desktop.searchButton.addEventListener( 'keydown', handleKeydown );
      this.desktop.searchButton.__headerHandlers = { handleMouseEnter, handleMouseLeave, handleClick, handleKeydown, handleFocus };
    }

    if ( this.headerDesktop ) {
      this.headerDesktop.addEventListener( 'mouseenter', this.cancelScheduledDesktopClose );
      this.headerDesktop.addEventListener( 'mouseleave', this.scheduleDesktopClose );
    }

    this.addEventListener( 'mouseenter', this.cancelScheduledDesktopClose );
    this.addEventListener( 'mouseleave', this.scheduleDesktopClose );
  }

  teardownDesktopEvents () {
    this.desktop.headerLinks.forEach( link => {
      if ( !link.__headerHandlers ) return;
      const { handleOpen, handleClick, handleKeydown } = link.__headerHandlers;
      link.removeEventListener( 'mouseenter', handleOpen );
      link.removeEventListener( 'click', handleClick );
      link.removeEventListener( 'mouseleave', this.scheduleDesktopClose );
      link.removeEventListener( 'keydown', handleKeydown );
      delete link.__headerHandlers;
    } );

    if ( this.desktop.searchButton ) {
      if ( this.desktop.searchButton.__headerHandlers ) {
        const { handleMouseEnter, handleMouseLeave, handleClick, handleKeydown, handleFocus } = this.desktop.searchButton.__headerHandlers;
        this.desktop.searchButton.removeEventListener( 'mouseenter', handleMouseEnter );
        this.desktop.searchButton.removeEventListener( 'focus', handleFocus );
        this.desktop.searchButton.removeEventListener( 'mouseleave', handleMouseLeave );
        this.desktop.searchButton.removeEventListener( 'click', handleClick );
        this.desktop.searchButton.removeEventListener( 'keydown', handleKeydown );
        delete this.desktop.searchButton.__headerHandlers;
      }
    }

    if ( this.headerDesktop ) {
      this.headerDesktop.removeEventListener( 'mouseenter', this.cancelScheduledDesktopClose );
      this.headerDesktop.removeEventListener( 'mouseleave', this.scheduleDesktopClose );
    }

    this.removeEventListener( 'mouseenter', this.cancelScheduledDesktopClose );
    this.removeEventListener( 'mouseleave', this.scheduleDesktopClose );
  }

  bindMobileEvents () {
    if ( !this.headerMobile ) return;

    this.refreshMobileElements();

    if ( this.header.mobileBurgerButton ) {
      this.mobileBurgerClickHandler = event => {
        event.preventDefault();
        this.openMobileMenu();
      };
      this.mobileBurgerKeydownHandler = event => {
        const key = event.key;
        if ( key !== 'Enter' && key !== ' ' && key !== 'Spacebar' ) return;
        event.preventDefault();
        this.openMobileMenu();
      };

      this.header.mobileBurgerButton.addEventListener( 'click', this.mobileBurgerClickHandler );
      this.header.mobileBurgerButton.addEventListener( 'keydown', this.mobileBurgerKeydownHandler );
    }

    if ( this.mobile.closeMenuBtn ) {
      this.mobileCloseHandler = event => {
        event.preventDefault();
        this.closeMobileMenu();
      };
      this.mobile.closeMenuBtn.addEventListener( 'click', this.mobileCloseHandler );
    }

    if ( this.mobile.overlay ) {
      this.mobileOverlayHandler = () => this.closeMobileMenu();
      this.mobile.overlay.addEventListener( 'click', this.mobileOverlayHandler );
    }

    if ( this.mobile.searchButton ) {
      this.mobileSearchHandler = event => {
        event.preventDefault();
        this.openMobileSearchPanel();
      };
      this.mobile.searchButton.addEventListener( 'click', this.mobileSearchHandler );
    }

    if ( this.header.searchButton ) {
      const shouldHandleMobileSearch = () => {
        if ( typeof window === 'undefined' || !window.matchMedia ) return true;
        return !window.matchMedia( '(min-width: 1024px)' ).matches;
      };

      this.headerSearchClickHandler = event => {
        if ( !shouldHandleMobileSearch() ) return;
        event.preventDefault();
        this.openMobileSearchPanel();
      };
      this.headerSearchKeydownHandler = event => {
        if ( !shouldHandleMobileSearch() ) return;
        const key = event.key;
        if ( key !== 'Enter' && key !== ' ' && key !== 'Spacebar' ) return;
        event.preventDefault();
        this.openMobileSearchPanel();
      };

      this.header.searchButton.addEventListener( 'click', this.headerSearchClickHandler );
      this.header.searchButton.addEventListener( 'keydown', this.headerSearchKeydownHandler );
    }

    this.mobileSubmenuHandlers = [];
    this.mobile.submenuLinks?.forEach( ( link, index ) => {
      const handler = event => this.handleMobileSubmenuClick( event );
      this.mobileSubmenuHandlers[index] = handler;
      link.addEventListener( 'click', handler );
    } );

    this.mobileBackHandlers = [];
    this.mobile.backButtons?.forEach( ( button, index ) => {
      const handler = event => this.handleMobileBackButtonClick( event );
      this.mobileBackHandlers[index] = handler;
      button.addEventListener( 'click', handler );
    } );
  }

  teardownMobileEvents () {
    if ( this.header.mobileBurgerButton ) {
      if ( this.mobileBurgerClickHandler ) {
        this.header.mobileBurgerButton.removeEventListener( 'click', this.mobileBurgerClickHandler );
      }
      if ( this.mobileBurgerKeydownHandler ) {
        this.header.mobileBurgerButton.removeEventListener( 'keydown', this.mobileBurgerKeydownHandler );
      }
    }

    if ( this.mobile.closeMenuBtn && this.mobileCloseHandler ) {
      this.mobile.closeMenuBtn.removeEventListener( 'click', this.mobileCloseHandler );
    }

    if ( this.mobile.overlay && this.mobileOverlayHandler ) {
      this.mobile.overlay.removeEventListener( 'click', this.mobileOverlayHandler );
    }

    if ( this.mobile.searchButton && this.mobileSearchHandler ) {
      this.mobile.searchButton.removeEventListener( 'click', this.mobileSearchHandler );
    }

    if ( this.header.searchButton ) {
      if ( this.headerSearchClickHandler ) {
        this.header.searchButton.removeEventListener( 'click', this.headerSearchClickHandler );
      }
      if ( this.headerSearchKeydownHandler ) {
        this.header.searchButton.removeEventListener( 'keydown', this.headerSearchKeydownHandler );
      }
    }

    if ( this.mobile.submenuLinks && this.mobileSubmenuHandlers ) {
      this.mobile.submenuLinks.forEach( ( link, index ) => {
        if ( this.mobileSubmenuHandlers[index] ) {
          link.removeEventListener( 'click', this.mobileSubmenuHandlers[index] );
        }
      } );
    }

    if ( this.mobile.backButtons && this.mobileBackHandlers ) {
      this.mobile.backButtons.forEach( ( button, index ) => {
        if ( this.mobileBackHandlers[index] ) {
          button.removeEventListener( 'click', this.mobileBackHandlers[index] );
        }
      } );
    }

    this.mobileBurgerClickHandler = null;
    this.mobileBurgerKeydownHandler = null;
    this.mobileCloseHandler = null;
    this.mobileOverlayHandler = null;
    this.mobileSearchHandler = null;
    this.headerSearchClickHandler = null;
    this.headerSearchKeydownHandler = null;
    this.mobileSubmenuHandlers = null;
    this.mobileBackHandlers = null;
  }

  handleDesktopSearchInteraction () {
    this.openDesktopSearch( { shouldFocus: true } );
  }

  openDesktopPanel ( trigger, { shouldFocus = true } = {} ) {
    if ( !trigger || !this.desktop.headerPanel ) return;

    this.cancelScheduledDesktopClose();

    const controlledId = trigger.getAttribute( 'aria-controls' );
    if ( !controlledId ) return;

    const controlledPanel = this.desktop.headerPanel.querySelector( `#${CSS.escape( controlledId )}` );
    if ( !controlledPanel ) return;

    this.desktop.headerLinks.forEach( link => {
      const isActive = link === trigger;
      link.setAttribute( 'aria-expanded', isActive ? 'true' : 'false' );
    } );

    if ( this.desktop.searchButton ) {
      this.desktop.searchButton.setAttribute( 'aria-expanded', 'false' );
    }

    this.showFadeElement( this.headerDesktop );
    this.showFadeElement( this.desktop.headerPanel );
    this.hideFadeElement( this.desktop.headerSearch );

    this.desktopPanels.forEach( panel => {
      if ( panel === controlledPanel ) {
        this.showFadeElement( panel );
      } else {
        this.hideFadeElement( panel );
      }
    } );

    this.markDesktopOpen();
    if ( shouldFocus ) {
      this.scheduleFocusTrapActivation( controlledPanel );
    } else if ( this.focusTrapActive ) {
      this.scheduleFocusTrapActivation( null );
    }
  }

  openDesktopSearch ( { shouldFocus = true } = {} ) {
    if ( !this.desktop.headerSearch ) return;

    this.cancelScheduledDesktopClose();

    this.desktop.headerLinks.forEach( link => link.setAttribute( 'aria-expanded', 'false' ) );

    if ( this.desktop.searchButton ) {
      this.desktop.searchButton.setAttribute( 'aria-expanded', 'true' );
    }

    this.showFadeElement( this.headerDesktop );
    this.hideFadeElement( this.desktop.headerPanel );
    this.desktopPanels.forEach( panel => this.hideFadeElement( panel ) );
    this.showFadeElement( this.desktop.headerSearch );

    this.markDesktopOpen();
    if ( shouldFocus ) {
      this.scheduleFocusTrapActivation( this.desktop.headerSearch );
    } else if ( this.focusTrapActive ) {
      this.scheduleFocusTrapActivation( null );
    }
  }

  markDesktopOpen () {
    this.isDesktopOpen = true;
    this.forceOpaque = true;
    this.applyScrollLock( true );
    this.updateTransparency();
  }

  closeDesktopMenu () {
    if ( !this.isDesktopOpen ) return;

    this.deactivateFocusTrap();
    if ( this.focusActivationTimeout ) {
      clearTimeout( this.focusActivationTimeout );
      this.focusActivationTimeout = null;
    }

    this.desktop.headerLinks.forEach( link => link.setAttribute( 'aria-expanded', 'false' ) );
    if ( this.desktop.searchButton ) {
      this.desktop.searchButton.setAttribute( 'aria-expanded', 'false' );
    }

    this.hideFadeElement( this.headerDesktop );
    this.hideFadeElement( this.desktop.headerPanel );
    this.hideFadeElement( this.desktop.headerSearch );
    this.desktopPanels.forEach( panel => this.hideFadeElement( panel ) );

    this.isDesktopOpen = false;
    this.forceOpaque = false;
    if ( !this.isMobileOpen ) {
      this.applyScrollLock( false );
    }
    this.updateTransparency();
  }

  showFadeElement ( element ) {
    if ( !element ) return;

    const existingTimer = this.fadeHideTimers.get( element );
    if ( existingTimer ) {
      clearTimeout( existingTimer );
      this.fadeHideTimers.delete( element );
    }

    if ( element.classList.contains( 'hidden' ) ) {
      element.classList.remove( 'hidden' );
    }

    if ( !element.classList.contains( 'fade' ) ) {
      element.classList.add( 'fade' );
    }

    requestAnimationFrame( () => {
      element.classList.add( 'show' );
    } );
  }

  hideFadeElement ( element ) {
    if ( !element ) return;

    element.classList.remove( 'show' );

    const timeout = window.setTimeout( () => {
      element.classList.add( 'hidden' );
      this.fadeHideTimers.delete( element );
    }, this.animationDuration );

    this.fadeHideTimers.set( element, timeout );
  }

  scheduleDesktopClose () {
    if ( this.desktopCloseTimeout ) {
      clearTimeout( this.desktopCloseTimeout );
    }

    this.desktopCloseTimeout = window.setTimeout( () => {
      this.closeDesktopMenu();
    }, 200 );
  }

  cancelScheduledDesktopClose () {
    if ( this.desktopCloseTimeout ) {
      clearTimeout( this.desktopCloseTimeout );
      this.desktopCloseTimeout = null;
    }
  }

  handleFocusOut ( event ) {
    if ( !this.isDesktopOpen ) return;
    if ( event.relatedTarget && this.contains( event.relatedTarget ) ) return;
    this.scheduleDesktopClose();
  }

  handleScroll () {
    this.updateTransparency();
  }

  handleResize () {
    this.updateTransparency();
  }

  handleEscape ( event ) {
    if ( event.key !== 'Escape' ) return;
    this.closeDesktopMenu();
    this.closeMobileMenu();
  }

  updateTransparency () {
    if ( !this.isDesktopOpen && !this.isMobileOpen && this.scrollLockApplied ) {
      this.applyScrollLock( false );
    }

    if ( this.forceOpaque ) {
      this.setTransparentState( false );
      return;
    }

    if ( !this.firstSection ) {
      this.setTransparentState( false );
      return;
    }

    const headerHeight = this.offsetHeight || 0;
    const firstSectionRect = this.firstSection.getBoundingClientRect();
    const isPastFirstSection = firstSectionRect.bottom <= headerHeight;

    this.setTransparentState( !isPastFirstSection );
  }

  setTransparentState ( value ) {
    this.setAttribute( 'transparent', value ? 'true' : 'false' );
    this.announcementBar?.setAttribute( 'transparent', value ? 'true' : 'false' );
  }

  applyScrollLock ( enable ) {
    if ( enable ) {
      if ( this.scrollLockApplied ) return;

      const top = window.scrollY || document.documentElement.scrollTop || 0;
      const left = window.scrollX || document.documentElement.scrollLeft || 0;

      this.lockedScrollPosition = { top, left };
      this.originalBodyPaddingRight = document.body.style.paddingRight;

      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      if ( scrollbarWidth > 0 ) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }

      document.body.style.position = 'fixed';
      document.body.style.top = `-${top}px`;
      document.body.style.left = `-${left}px`;
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      document.body.classList.add( 'scroll-locked' );
      document.documentElement.classList.add( 'scroll-locked' );

      this.pausePageVideos();

      this.scrollLockApplied = true;
      return;
    }

    if ( !this.scrollLockApplied ) return;

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    document.body.style.paddingRight = this.originalBodyPaddingRight;

    window.scrollTo( {
      top: this.lockedScrollPosition.top,
      left: this.lockedScrollPosition.left,
      behavior: 'auto'
    } );

    document.body.classList.remove( 'scroll-locked' );
    document.documentElement.classList.remove( 'scroll-locked' );

    this.resumePageVideos();

    this.scrollLockApplied = false;
    this.lockedScrollPosition = { top: 0, left: 0 };
    this.originalBodyPaddingRight = '';
  }

  pausePageVideos () {
    this.previouslyPlayingVideos.clear();

    document.querySelectorAll( 'video' ).forEach( video => {
      if ( !( video instanceof HTMLVideoElement ) ) return;

      const wasPlaying = !video.paused && !video.ended;

      if ( wasPlaying ) {
        this.previouslyPlayingVideos.add( video );
      }

      if ( typeof video.pause === 'function' ) {
        try {
          video.pause();
        } catch ( error ) {
          console.error( 'Unable to pause video:', error );
        }
      }
    } );
  }

  resumePageVideos () {
    if ( this.previouslyPlayingVideos.size === 0 ) return;

    this.previouslyPlayingVideos.forEach( video => {
      if ( !( video instanceof HTMLVideoElement ) ) return;
      if ( !document.contains( video ) ) return;
      if ( typeof video.play !== 'function' ) return;

      try {
        const playPromise = video.play();
        if ( playPromise?.catch ) {
          playPromise.catch( error => {
            console.warn( 'Unable to resume video:', error );
          } );
        }
      } catch ( error ) {
        console.warn( 'Unable to resume video:', error );
      }
    } );

    this.previouslyPlayingVideos.clear();
  }

  getFocusableElements ( root = this.headerDesktop || this ) {
    if ( !root ) return [];

    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join( ', ' );

    const elements = Array.from( root.querySelectorAll( focusableSelectors ) );

    return elements.filter( element => {
      if ( element.hasAttribute( 'disabled' ) ) return false;
      if ( element.getAttribute( 'aria-hidden' ) === 'true' ) return false;
      if ( element.closest( '[hidden]' ) ) return false;

      const style = window.getComputedStyle( element );
      if ( style.visibility === 'hidden' || style.display === 'none' ) return false;

      return element.getClientRects().length > 0;
    } );
  }

  focusFirstElement ( root = this.focusTrapRoot || this.headerDesktop || this ) {
    const elements = this.getFocusableElements( root );
    if ( !elements.length ) return false;

    const first = elements[0];
    try {
      first.focus( { preventScroll: true } );
      return true;
    } catch ( error ) {
      console.warn( 'Unable to focus element:', error );
      return false;
    }
  }

  activateFocusTrap ( initialFocusRoot = this.headerDesktop || this ) {
    const wasActive = this.focusTrapActive;
    this.focusTrapActive = true;
    const focusRoot = initialFocusRoot || this.headerDesktop || this;
    this.focusTrapRoot = focusRoot;
    this.focusableElements = this.getFocusableElements( focusRoot );

    if ( !this.focusableElements.length ) {
      if ( !wasActive ) {
        this.focusTrapActive = false;
        this.focusTrapRoot = null;
      }
      return;
    }

    if ( !wasActive ) {
      this.previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;

      document.addEventListener( 'focusin', this.handleFocusTrapFocusIn, true );
      this.addEventListener( 'keydown', this.handleFocusTrapKeydown );
    }

    if ( initialFocusRoot ) {
      if ( !this.focusFirstElement( initialFocusRoot ) ) {
        this.focusFirstElement( this.focusTrapRoot );
      }
    } else {
      this.focusFirstElement( this.focusTrapRoot );
    }
  }

  deactivateFocusTrap () {
    if ( !this.focusTrapActive ) return;

    document.removeEventListener( 'focusin', this.handleFocusTrapFocusIn, true );
    this.removeEventListener( 'keydown', this.handleFocusTrapKeydown );

    this.focusTrapActive = false;
    this.focusableElements = [];
    this.focusTrapRoot = null;
    if ( this.focusActivationTimeout ) {
      clearTimeout( this.focusActivationTimeout );
      this.focusActivationTimeout = null;
    }

    const target = this.previousFocus;
    this.previousFocus = null;

    if ( target && typeof target.focus === 'function' && target.isConnected ) {
      try {
        target.focus( { preventScroll: true } );
      } catch ( error ) {
        console.warn( 'Unable to restore focus:', error );
      }
    }
  }

  handleFocusTrapKeydown ( event ) {
    if ( !this.focusTrapActive ) return;
    if ( event.key !== 'Tab' ) return;

    const focusable = this.getFocusableElements( this.focusTrapRoot );
    if ( !focusable.length ) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if ( event.shiftKey ) {
      if ( active === first || !this.focusTrapRoot?.contains( active ) ) {
        event.preventDefault();
        last.focus( { preventScroll: true } );
      }
      return;
    }

    if ( active === last || !this.focusTrapRoot?.contains( active ) ) {
      event.preventDefault();
      first.focus( { preventScroll: true } );
    }
  }

  handleFocusTrapFocusIn ( event ) {
    if ( !this.focusTrapActive ) return;
    if ( !this.focusTrapRoot ) return;
    if ( this.focusTrapRoot.contains( event.target ) ) return;

    const focusable = this.getFocusableElements( this.focusTrapRoot );
    if ( !focusable.length ) return;

    focusable[0].focus( { preventScroll: true } );
  }

  scheduleFocusTrapActivation ( focusRoot ) {
    if ( this.focusActivationTimeout ) {
      clearTimeout( this.focusActivationTimeout );
    }

    this.focusActivationTimeout = window.setTimeout( () => {
      this.focusActivationTimeout = null;

      if ( !this.isDesktopOpen && !this.isMobileOpen ) return;

      if ( focusRoot ) {
        this.activateFocusTrap( focusRoot );
        return;
      }

      if ( this.focusTrapActive ) {
        const root = this.focusTrapRoot || this.headerDesktop || this.headerMobile || this;
        this.focusableElements = this.getFocusableElements( root );
      }
    }, this.animationDuration );
  }

  refreshMobileElements () {
    if ( !this.headerMobile ) return;

    this.mobile.overlay = this.headerMobile.querySelector( '[overlay]' );
    this.mobile.closeMenuBtn = this.headerMobile.querySelector( '#close-menu-button' );
    this.mobile.mainMenuPanel = this.headerMobile.querySelector( '#main-menu-panel' );
    this.mobile.searchPanel = this.headerMobile.querySelector( '#search-panel' );
    this.mobile.searchButton = this.headerMobile.querySelector( 'button[data-submenu="search-panel"]' );
    this.mobile.submenuLinks = this.headerMobile.querySelectorAll( '[data-submenu]:not(button[data-submenu="search-panel"])' );
    this.mobile.backButtons = this.headerMobile.querySelectorAll( 'button[data-target]' );
    this.mobile.submenus = this.headerMobile.querySelectorAll( '[data-parent]' );
  }

  openMobileMenu () {
    if ( !this.headerMobile || this.isMobileOpen ) return;

    this.refreshMobileElements();

    this.isMobileOpen = true;
    this.header.mobileBurgerButton?.setAttribute( 'aria-expanded', 'true' );

    this.headerMobile.classList.add( 'visible' );
    requestAnimationFrame( () => {
      this.headerMobile?.classList.add( 'show' );
    } );

    this.mobile.overlay?.classList.add( 'show' );

    this.resetMobileMenuState();

    this.applyScrollLock( true );

    if ( this.focusActivationTimeout ) {
      clearTimeout( this.focusActivationTimeout );
    }
    this.focusActivationTimeout = window.setTimeout( () => {
      this.focusActivationTimeout = null;
      if ( !this.isMobileOpen ) return;
      this.activateFocusTrap( this.headerMobile );
    }, this.animationDuration );
  }

  closeMobileMenu () {
    if ( !this.headerMobile || !this.isMobileOpen ) return;

    this.isMobileOpen = false;
    this.header.mobileBurgerButton?.setAttribute( 'aria-expanded', 'false' );

    this.headerMobile.classList.remove( 'show' );
    this.mobile.overlay?.classList.remove( 'show' );

    window.setTimeout( () => {
      this.headerMobile?.classList.remove( 'visible' );
      this.resetMobileMenuState();
    }, this.animationDuration );

    this.deactivateFocusTrap();

    if ( !this.isDesktopOpen ) {
      this.applyScrollLock( false );
    }
  }

  resetMobileMenuState () {
    if ( !this.headerMobile ) return;

    this.mobile.mainMenuPanel?.classList.add( 'active' );
    this.mobile.mainMenuPanel?.classList.remove( 'exit-to-left', 'exit-to-right', 'enter-from-left', 'enter-from-right' );

    this.mobile.submenus?.forEach( panel => {
      panel.classList.remove( 'active', 'exit-to-left', 'exit-to-right', 'enter-from-left', 'enter-from-right' );
    } );

    this.mobile.searchPanel?.classList.remove( 'active', 'exit-to-left', 'exit-to-right', 'enter-from-left', 'enter-from-right' );

    this.mobile.submenuLinks?.forEach( link => link.setAttribute( 'aria-expanded', 'false' ) );
    this.mobile.searchButton?.setAttribute( 'aria-expanded', 'false' );
  }

  handleMobileSubmenuClick ( event ) {
    event.preventDefault();
    const trigger = event.currentTarget;
    const targetId = trigger?.getAttribute( 'data-submenu' );
    const targetPanel = targetId ? this.headerMobile?.querySelector( `#${CSS.escape( targetId )}` ) : null;
    if ( !targetPanel ) return;

    const currentPanel = trigger.closest( '[id]' );
    this.transitionMobilePanels( currentPanel, targetPanel, 'forward' );

    trigger.setAttribute( 'aria-expanded', 'true' );

    window.setTimeout( () => {
      const firstFocusable = this.getFocusableElements( targetPanel )[0];
      firstFocusable?.focus();
    }, this.animationDuration );
  }

  handleMobileBackButtonClick ( event ) {
    event.preventDefault();
    const trigger = event.currentTarget;
    const targetId = trigger?.getAttribute( 'data-target' );
    const targetPanel = targetId ? this.headerMobile?.querySelector( `#${CSS.escape( targetId )}` ) : null;
    if ( !targetPanel ) return;

    const currentPanel = trigger.closest( '[id]' );
    this.transitionMobilePanels( currentPanel, targetPanel, 'backward' );

    const opener = currentPanel
      ? this.headerMobile?.querySelector( `[data-submenu="${CSS.escape( currentPanel.id )}"]` )
      : null;

    opener?.setAttribute( 'aria-expanded', 'false' );

    window.setTimeout( () => {
      if ( currentPanel?.id === 'search-panel' ) {
        this.mobile.searchButton?.focus();
        return;
      }

      const focusTarget =
        opener ||
        this.headerMobile?.querySelector( `[data-submenu="${CSS.escape( targetPanel.id )}"]` ) ||
        this.getFocusableElements( targetPanel )[0];

      focusTarget?.focus();
    }, this.animationDuration );
  }

  openMobileSearchPanel () {
    if ( !this.headerMobile ) return;

    if ( !this.isMobileOpen ) {
      this.openMobileMenu();
    }

    const currentPanel = this.headerMobile.querySelector( '[id].active' ) || this.mobile.mainMenuPanel;
    const targetPanel = this.mobile.searchPanel;

    if ( !targetPanel ) return;

    this.mobile.searchButton?.setAttribute( 'aria-expanded', 'true' );

    if ( currentPanel ) {
      currentPanel.classList.remove( 'active', 'exit-to-left', 'exit-to-right', 'enter-from-left', 'enter-from-right' );
    }

    targetPanel.classList.remove( 'enter-from-right', 'enter-from-left', 'exit-to-left', 'exit-to-right' );
    targetPanel.classList.add( 'active' );

    const searchInput = targetPanel.querySelector( 'input[type="search"]' );
    if ( searchInput ) {
      searchInput.focus();
    }
  }

  transitionMobilePanels ( currentPanel, targetPanel, direction = 'forward' ) {
    if ( !targetPanel || currentPanel === targetPanel ) {
      targetPanel?.classList.add( 'active' );
      return;
    }

    const exitClass = direction === 'forward' ? 'exit-to-left' : 'exit-to-right';
    const enterClass = direction === 'forward' ? 'enter-from-right' : 'enter-from-left';

    currentPanel?.classList.remove( 'enter-from-right', 'enter-from-left', 'exit-to-left', 'exit-to-right', 'active' );
    currentPanel?.classList.add( exitClass );

    targetPanel.classList.remove( 'enter-from-right', 'enter-from-left', 'exit-to-left', 'exit-to-right' );
    targetPanel.classList.add( 'active', enterClass );

    window.setTimeout( () => {
      currentPanel?.classList.remove( exitClass );
      targetPanel?.classList.remove( enterClass );
    }, this.animationDuration );
  }

}

customElements.define( 'header-menu', HeaderMenu );





/**
 * Shopify Predictive Search Implementation for Che Studios
 */
class HeaderSearch extends HTMLElement {
  constructor () {
    super();
    this.input = this.querySelector( 'input[type="search"]' );
    this.suggestionsContainer = this.querySelector( '.suggestions ul' );
    this.defaultSuggestions = ['Swimshort', 'Underwear', 'Baller', 'Valbonne'];

    this.setupEventListeners();
    this.displayDefaultSuggestions();
  }

  setupEventListeners () {
    this.input?.addEventListener( 'input', this.debounce( ( event ) => {
      const searchTerm = event.target.value.trim();

      if ( searchTerm.length >= 2 ) {
        this.getSearchResults( searchTerm );
      } else if ( searchTerm.length === 0 ) {
        this.displayDefaultSuggestions();
      }
    }, 300 ) );
  }

  async getSearchResults ( searchTerm ) {
    try {
      const response = await fetch( `/search/suggest.json?q=${encodeURIComponent( searchTerm )}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last` );

      if ( !response.ok ) {
        throw new Error( `Search request failed with status: ${response.status}` );
      }

      const data = await response.json();
      this.displaySearchResults( data.resources.results );
    } catch ( error ) {
      console.error( 'Error fetching search results:', error );
      this.displayDefaultSuggestions();
    }
  }

  displaySearchResults ( results ) {
    this.suggestionsContainer.innerHTML = '';

    if ( !results.products || results.products.length === 0 ) {
      this.displayNoResultsMessage();
      return;
    }

    results.products.forEach( product => {
      const listItem = document.createElement( 'li' );

      const link = document.createElement( 'a' );
      link.href = product.url;
      link.className = 'block hover:text-black transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-black';
      link.textContent = product.title;

      listItem.appendChild( link );
      this.suggestionsContainer.appendChild( listItem );
    } );
  }

  displayDefaultSuggestions () {
    if ( !this.suggestionsContainer ) return;

    this.suggestionsContainer.innerHTML = '';
    this.defaultSuggestions.forEach( suggestion => {
      const listItem = document.createElement( 'li' );

      const link = document.createElement( 'a' );
      link.href = `/search?q=${encodeURIComponent( suggestion )}&type=product`;
      link.className = 'block hover:text-black transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-black';
      link.textContent = suggestion;

      listItem.appendChild( link );
      this.suggestionsContainer.appendChild( listItem );
    } );
  }

  displayNoResultsMessage () {
    this.suggestionsContainer.innerHTML = '';
    const listItem = document.createElement( 'li' );
    listItem.textContent = 'No results found';
    this.suggestionsContainer.appendChild( listItem );
  }

  debounce ( fn, delay ) {
    let timeout;
    return ( ...args ) => {
      clearTimeout( timeout );
      timeout = setTimeout( () => fn.apply( this, args ), delay );
    };
  }
}

customElements.define( 'header-search', HeaderSearch );
